import React, { useState } from "react";
import { Grid, Button, CircularProgress, Stack } from "@mui/material";
import CustomSelect from "../CustomSelect";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const BalanceReport = ({ customers }) => {
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [balanceFilters, setBalanceFilters] = useState({
    customerId: "",
  });

  const generateBalanceReport = async (exportType = 'html') => {
    const endpoint = balanceFilters.customerId
      ? `reports/customer-balances?customerId=${balanceFilters.customerId}`
      : `reports/customer-balances`;

    setLoadingBalance(true);
    try {
      const response = await api.get(endpoint);

      if (response.data && response.data.status) {
        const customerName = balanceFilters.customerId
          ? customers.find(c => c._id === balanceFilters.customerId)?.name || "Unknown"
          : "All Customers";

        if (exportType === 'excel') {
          let excelData = [];

          if (balanceFilters.customerId) {
            excelData = [{
              "Customer Name": response.data.customer.name,
              "Balance": response.data.customer.balance
            }];
          } else {
            if (Array.isArray(response.data.customers)) {
              excelData = response.data.customers.map(customer => ({
                "Customer Name": customer.name,
                "Balance": customer.balance
              }));
            } else {
              excelData = [{
                "Customer Name": response.data.customer.name,
                "Balance": response.data.customer.balance
              }];
            }
          }

          exportToExcel(excelData, `Balance_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }

        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Customer Balance Report</title>
            <style>
               body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
              h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 12px; }
              .customer-info { margin: 20px 20px; font-size: 14px;text-align:right; margin-right:30px; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Customer Balance Report${balanceFilters.customerId ? `: ${customerName}` : ''}</h1>
              ${!balanceFilters.customerId ? `<div class="report-filters"><p><strong>Showing:</strong> ${customerName}</p></div>` : ''}
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
        `;

        if (balanceFilters.customerId) {
          const customer = response.data.customer;

          htmlContent += `
            <div class="customer-info">
              <p><strong>Customer:</strong> ${customer.name}</p>
              <p><strong>Balance:</strong> $${customer.balance}</p>
            </div>
          `;
        } else {
          htmlContent+=`<div class="customer-info">
          <p>Total Balance: ${response?.data?.totalBalance}</p>
            </div>`;
          htmlContent += `
            <table>
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
          `;

          if (Array.isArray(response.data.customers)) {
            response.data.customers.forEach(customer => {
              htmlContent += `
                <tr>
                  <td>${customer.name}</td>
                  <td>$${customer.balance}</td>
                </tr>
              `;
            });
          } else {
            htmlContent += `
              <tr>
                <td>${response.data.customer.name}</td>
                <td>$${response.data.customer.balance}</td>
              </tr>
            `;
          }

          htmlContent += `
              </tbody>
            </table>
          `;
        }
        // htmlContent+=`<div class="customer-info">
        //   <p>Total Balance: ${response?.data?.totalBalance}</p>
        //     </div>`;
        htmlContent += `
            <div class="export-btn-container">
            </div>
          </body>
          </html>
        `;

        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `Balance_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }

        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingBalance(false);
          return;
        }

        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }

        reportWindow.document.write(htmlContent);
        reportWindow.document.close();

        window.addEventListener('message', function (event) {
          if (event.data.type === 'exportPDF' && event.data.reportType === 'balance') {
            generateBalanceReport('pdf');
          } else if (event.data.type === 'exportExcel' && event.data.reportType === 'balance') {
            generateBalanceReport('excel');
          }
        });

        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected customer");
      }
    } catch (error) {
      console.error("Error generating balance report:", error);
      toast.error("Failed to generate balance report");
    } finally {
      setLoadingBalance(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const endpoint = balanceFilters.customerId
        ? `reports/customer-balances?customerId=${balanceFilters.customerId}`
        : `reports/customer-balances`;

      const response = await api.get(endpoint);

      if (response.data && response.data.status) {
        const customerName = balanceFilters.customerId
          ? customers.find(c => c._id === balanceFilters.customerId)?.name || "Unknown"
          : "All Customers";

        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Customer Balance Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #1abc9c; }
              table { border-collapse: collapse; width: 100%; margin-top: 20px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
              .customer-info { margin: 20px 0; font-size: 14px;text-align:right; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Customer Balance Report${balanceFilters.customerId ? `: ${customerName}` : ''}</h1>
              ${!balanceFilters.customerId ? `<div class="report-filters"><p><strong>Showing:</strong> ${customerName}</p></div>` : ''}
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
        `;

        if (balanceFilters.customerId) {
          const customer = response.data.customer;

          htmlContent += `
            <div class="customer-info">
              <p><strong>Customer:</strong> ${customer.name}</p>
              <p><strong>Balance:</strong> $${customer.balance}</p>
            </div>
          `;
        } else {
          htmlContent += `
            <table>
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
          `;

          if (Array.isArray(response.data.customers)) {
            response.data.customers.forEach(customer => {
              htmlContent += `
                <tr>
                  <td>${customer.name}</td>
                  <td>$${customer.balance}</td>
                </tr>
              `;
            });
          } else {
            htmlContent += `
              <tr>
                <td>${response.data.customer.name}</td>
                <td>$${response.data.customer.balance}</td>
              </tr>
            `;
          }

          htmlContent += `
              </tbody>
            </table>
          `;
        }

        htmlContent += `
          </body>
          </html>
        `;

        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);

        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `Balance_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={10}>
        <CustomSelect
          label="Customer"
          value={balanceFilters.customerId}
          onChange={(e) => setBalanceFilters({ ...balanceFilters, customerId: e.target.value })}
          options={[
            { id: "", name: "All Customers" },
            ...customers.map((customer) => ({
              id: customer._id,
              name: customer.name,
            })),
          ]}
          fullWidth
          sx={{ minWidth: "100%" }}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateBalanceReport('html')}
            disabled={loadingBalance || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{
              height: "38px",
              backgroundColor: colors.primary,
              '&:hover': {
                backgroundColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            {loadingBalance ? <CircularProgress size={24} color="inherit" /> : "View"}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateBalanceReport('pdf')}
            disabled={loadingBalance || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateBalanceReport('excel')}
            disabled={loadingBalance || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateBalanceReport('email')}
            disabled={loadingBalance || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default BalanceReport;
